import { 
  getCustomizeControls,
  getCustomizeControlsActions,
  getCustomizeControlsAiOpenStates,
  useCustomizeControlsStore,
  type NistCsfControl
} from "@/modules/agent/states/cusomize-controls";
import { Checkbox, Tooltip } from "@nextui-org/react";
import { Info, Upload } from "lucide-react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { cn } from "@/shared/utils/classname-merger";
import { ZInputWithOnChangeDelay } from "../helpers";
import { ZButton } from "@/shared/components/button";
import UploadDocumentsModal from "../UploadDocumentsModal";
import ReassessDocumentsModal from "../ReassessDocumentsModal";
import { NistCsfControl as NistCsfControlType, TSCABBRIVATIONS } from "@/modules/agent/states/cusomize-controls";
import { useReassessmentModeStore } from "@/modules/agent/states";
import { useParams } from "react-router-dom";
import capitalizeFirstLetter from "@/shared/utils/capitalize-first-letter";

export const CustomizeControlNistCsfIdRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  selectedControls,
  setSelectedControls,
  showCheckboxes = false,
}: {
  control: NistCsfControlType;
  handleChangeDataState: (
    key: keyof NistCsfControlType,
    uId: string,
    newValue: string,
    changesInTestingProcedure?: number
  ) => void;
  searchQuery: string;
  selectedControls: Set<string>;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  showCheckboxes?: boolean;
}) => {
  const [isActive, setIsActive] = useState(false);

  const listOfIds = (getCustomizeControls() as NistCsfControlType[])
    .filter((d) => d.uid !== control.uid)
    .map((d) => d.control_id);

  const isCheckboxDisabled = getCustomizeControlsAiOpenStates() > 0;
  const isDuplicateId = listOfIds.includes(control.control_id);
  const isInvalidId = control.control_id.trim().length === 0;

  return (
    <div className={cn("flex pl-2 items-center")}>
      {showCheckboxes && (
        <Checkbox
          isSelected={selectedControls.has(control.uid)}
          classNames={{
            base: cn("group", control.edited_state === "NEW" && "invisible"),
            wrapper: cn(
              "group-data-[disabled=true]:border-default",
              "after:group-data-[disabled=true]:bg-default-100"
            ),
            icon: "group-data-[disabled=true]:text-default-300",
          }}
          onChange={() => {
            setSelectedControls((prev) => {
              const newValue = new Set(prev);
              if (selectedControls.has(control.uid)) {
                newValue.delete(control.uid);
              } else {
                newValue.add(control.uid);
              }
              return newValue;
            });
          }}
          isDisabled={isCheckboxDisabled}
          disabled={isCheckboxDisabled}
          data-component="z-table-row-select"
          color={"primary"}
        />
      )}
      <div
        id={`${control.uid}-control-id`}
        aria-label={`${control.uid}-control-id`}
        className="pl-2 pt-1 whitespace-nowrap overflow-hidden text-ellipsis"
      >
        {control.control_id.toUpperCase()}
      </div>
    </div>
  );
};

export const CustomizeControlNistCsfCategoryRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
}: {
  control: NistCsfControlType;
  handleChangeDataState: (
    key: keyof NistCsfControlType,
    uId: string,
    newValue: string
  ) => void;
  searchQuery: string;
}) => {
  const value = control.category || "";
  
  return (
    <div className="flex items-center">
        <span>{value}</span>
    </div>
  );
};

export const CustomizeControlNistCsfFunctionRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
}: {
  control: NistCsfControlType;
  handleChangeDataState: (
    key: keyof NistCsfControlType,
    uId: string,
    newValue: string
  ) => void;
  searchQuery: string;
}) => {
  const value = control.subcategory || "";
  return (
    <div className="flex items-center">
        <span>{value}</span>
    </div>
  );
};

export const CustomizeControlNistCsfDocumentUploadRenderer = ({
  control,
  allControls,
  currentIndex,
  showCheckboxes = false
}: {
  control: NistCsfControlType;
  allControls?: NistCsfControlType[];
  currentIndex?: number;
  showCheckboxes?: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReassessModalOpen, setIsReassessModalOpen] = useState(false);
  const [activeControlIndex, setActiveControlIndex] = useState<number | undefined>(currentIndex);
  
  // Get reassessment mode state
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const { id: currentAgentId } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = isReassessmentMode && currentAgentId === reassessmentAgentId;
  
  // Get all controls from state if not provided
  const allNistControls = useMemo(() => {
    if (allControls) return allControls;
    const controls = getCustomizeControls() as NistCsfControlType[];
    return controls.filter(c => c.agentType === 'risk_assessment');
  }, [allControls]);
  
  // Calculate the active control based on the index
  const activeControl = useMemo(() => {
    if (activeControlIndex !== undefined && allNistControls && allNistControls.length > 0 && 
        activeControlIndex >= 0 && activeControlIndex < allNistControls.length) {
      return allNistControls[activeControlIndex];
    }
    return control;
  }, [activeControlIndex, allNistControls, control]);
  
  // Find current index if not provided
  const calculatedCurrentIndex = useMemo(() => {
    if (currentIndex !== undefined) return currentIndex;
    return allNistControls.findIndex(c => c.uid === control.uid);
  }, [currentIndex, control.uid, allNistControls]);
  
  // Update activeControlIndex when calculatedCurrentIndex changes
  useEffect(() => {
    setActiveControlIndex(calculatedCurrentIndex);
  }, [calculatedCurrentIndex]);

  const handlePrevControl = useCallback(() => {
    if (!allNistControls || activeControlIndex === undefined || activeControlIndex <= 0) return;
    setActiveControlIndex(activeControlIndex - 1);
  }, [activeControlIndex, allNistControls]);
  
  const handleNextControl = useCallback(() => {
    if (!allNistControls || activeControlIndex === undefined || activeControlIndex >= allNistControls.length - 1) return;
    setActiveControlIndex(activeControlIndex + 1);
  }, [activeControlIndex, allNistControls]);
  
  const handleUpdateSourceUrls = useCallback((newUrls: string[], newFileNames?: string[]) => {
    if (!activeControl) return;
    
    // In non-reassessment mode, update source_urls directly
    if (!isCurrentSessionInReassessment) {
      // Update the control's source_urls using the store's setDataState action
      getCustomizeControlsActions().setDataState((prevControls) => {
        return prevControls.map(c => {
          if (c.uid === activeControl.uid) {
            return { ...c, source_urls: newUrls, file_names: newFileNames || [] };
          }
          return c;
        });
      });
      
      // Trigger autosave by incrementing the changesMade counter
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
    } else {
      // In reassessment mode, keep permanent files and temporary files separate
      getCustomizeControlsActions().setDataState((prevControls) => {
        return prevControls.map(c => {
          if (c.uid === activeControl.uid) {
            return {
              ...c,
              // Keep original source_urls unchanged
              // Update the temporary files
              new_uploaded_files: newUrls,
              new_uploaded_file_names: newFileNames || []
            };
          }
          return c;
        });
      });
    }
  }, [activeControl, isCurrentSessionInReassessment]);
  
  const handleOpenModal = useCallback(() => {
    setActiveControlIndex(calculatedCurrentIndex);
    
    // Choose which modal to open based on reassessment mode
    if (isCurrentSessionInReassessment) {
      setIsReassessModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  }, [calculatedCurrentIndex, isCurrentSessionInReassessment]);

  // Get file count directly from the store
  const currentControl = getCustomizeControls().find(c => c.uid === control.uid) || control;
  const typedControl = currentControl as NistCsfControlType;
  const fileCount = (typedControl.source_urls?.length || 0) + (typedControl.new_uploaded_files?.length || 0);
  const hasUploadedFiles = fileCount > 0;

  const selectedTab = useCustomizeControlsStore().selectedTab;

  const uploadModalName = useMemo(() => {
      if(selectedTab && activeControl){
        const thisSelectedTab = selectedTab as keyof typeof TSCABBRIVATIONS
        const selectedTabAbbriviation = TSCABBRIVATIONS[thisSelectedTab]
        return `${activeControl.control_id?.replace(selectedTabAbbriviation, `${selectedTabAbbriviation} `).toUpperCase()} (${capitalizeFirstLetter(selectedTab)} / ${activeControl.category})`
      }
      return ""
    }, [selectedTab, activeControl.control_id, activeControl.control_id])

  return (
    <div className="flex justify-center">
      {hasUploadedFiles ? (
        <div className="flex items-center">
          <div className="flex items-center justify-center w-4 h-4 bg-gray-300 rounded-full mr-2">
            <span className="text-[10px] font-medium">{fileCount}</span>
          </div>
          <ZButton
            size="sm"
            variant="bordered"
            color="primary"
            className="min-w-0 w-12 h-6 p-0 flex justify-center items-center"
            onClick={handleOpenModal}
            isDisabled={showCheckboxes}
          >
            <Upload size={14} />
          </ZButton>
        </div>
      ) : (
        <ZButton
          size="sm"
          variant="solid"
          color="primary"
          className="px-3 py-1 h-auto"
          onClick={handleOpenModal}
          isDisabled={showCheckboxes}
        >
          Upload
        </ZButton>
      )}
      
      {/* Only show UploadDocumentsModal when NOT in reassessment mode for this session */}
      {!isCurrentSessionInReassessment && (
        <UploadDocumentsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          controlId={activeControl.control_id}
          controlName={uploadModalName}
          controlDescription={activeControl.subcategory || ""}
          onPrevControl={allNistControls && activeControlIndex !== undefined && activeControlIndex > 0 ? handlePrevControl : undefined}
          onNextControl={allNistControls && activeControlIndex !== undefined && activeControlIndex < (allNistControls.length - 1) ? handleNextControl : undefined}
          currentIndex={activeControlIndex}
          totalControls={allNistControls?.length}
          sourceUrls={activeControl.source_urls || []}
          fileNames={activeControl.file_names || []}
          onUpdateSourceUrls={handleUpdateSourceUrls}
        />
      )}
      
      {/* Only show ReassessDocumentsModal when in reassessment mode for this specific session */}
      {isCurrentSessionInReassessment && (
        <ReassessDocumentsModal
          isOpen={isReassessModalOpen}
          onClose={() => setIsReassessModalOpen(false)}
          agentId={currentAgentId || ""}
          controlName={uploadModalName}
          controlDescription={activeControl.subcategory || ""}
          control={activeControl}
          controlIndex={activeControlIndex}
          onPrevControl={allNistControls && activeControlIndex !== undefined && activeControlIndex > 0 ? handlePrevControl : undefined}
          onNextControl={allNistControls && activeControlIndex !== undefined && activeControlIndex < (allNistControls.length - 1) ? handleNextControl : undefined}
          totalControls={allNistControls?.length}
        />
      )}
    </div>
  );
};